<template>
  <div class="papers-page container py-4">
    <!-- 標題列 -->
    <div class="text-center mb-4">
      <h2 class="fw-bold mb-1 text-primary">
        {{ $t('paper.title') }}
      </h2>
      <p class="text-muted mb-0">
        {{ $t('paper.subtitle') }}
      </p>
    </div>

    <!-- 🔍 搜尋列 -->
    <div class="search-bar-wrapper mb-4">
      <div class="input-group search-input-group">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          :placeholder="$t('paper.placeholder')"
          @keyup.enter="handleSearchClick"
        />
        <button
          class="btn btn-primary px-4"
          type="button"
          @click="handleSearchClick"
        >
          {{ $t('paper.search') }}
        </button>
      </div>

      <!-- 常用關鍵字 -->
      <div class="quick-keywords mt-2">
        <button
          v-for="kw in quickKeywords"
          :key="kw"
          class="btn btn-outline-secondary btn-sm me-2 mb-2"
          type="button"
          @click="applyQuickKeyword(kw)"
        >
          {{ kw }}
        </button>

        <button
          v-if="searchQuery"
          class="btn btn-link btn-sm text-decoration-none text-muted"
          type="button"
          @click="clearSearch"
        >
          {{ $t('paper.clear') }}
        </button>
      </div>
    </div>

    <!-- 錯誤 -->
    <div v-if="errorMsg" class="alert alert-danger">
      {{ errorMsg }}
    </div>

    <!-- 載入中 -->
    <div v-if="loading" class="text-center py-5">
      {{ $t('common.loading') }}
    </div>

    <template v-else>
      <!-- 🔝 上方精選 -->
      <section v-if="featuredArticles.length" class="mb-5 featured-section">
        <div class="row g-3">
          <div
            v-for="item in featuredArticles"
            :key="item.id"
            class="col-12 col-sm-6 col-lg-4"
          >
            <div
              class="card h-100 article-card clickable"
              @click="openArticle(item)"
            >
              <!-- 封面 -->
              <div class="card-img-top-wrapper">
                <img
                  v-if="getCoverUrl(item)"
                  class="card-img-top"
                  :src="getCoverUrl(item)"
                  :alt="item.title"
                />
                <div
                  v-else
                  class="card-img-top placeholder d-flex align-items-center justify-content-center"
                >
                  <span class="text-muted small">
                    {{ $t('paper.noCover') }}
                  </span>
                </div>
              </div>

              <div class="card-body d-flex flex-column">
                <h5 class="card-title fw-bold mb-2 text-primary">
                  {{ displayTitle(item) }}
                </h5>

                <div class="mt-auto text-end">
                  <span class="text-primary small">{{ $t('paper.more') }} ›</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 🔻 下方列表 -->
      <section v-if="visibleOtherArticles.length">
        <div>
          <div
            v-for="(item, index) in visibleOtherArticles"
            :key="item.id"
            :ref="el => registerArticleEl(el, item.id)"
            :class="[
              'article-row d-flex mb-3 p-3 border rounded-3 clickable bg-white',
              { 'is-visible': isVisible(item.id) }
            ]"
            @click="openArticle(item)"
            :style="{ transitionDelay: (index * 60) + 'ms' }"
          >
            <!-- 左側圖片 -->
            <div v-if="getCoverUrl(item)" class="flex-shrink-0 me-3">
              <div class="thumb-wrapper">
                <img
                  class="thumb-img"
                  :src="getCoverUrl(item)"
                  :alt="item.title"
                />
              </div>
            </div>

            <!-- 右側內容 -->
            <div class="flex-grow-1 d-flex flex-column">
              <h6 class="mb-1 fw-bold text-primary">
                {{ displayTitle(item) }}
              </h6>

              <p class="small text-truncate-2 mb-2">
                {{ item.sub_title || $t('paper.subtitle') + '。' }}
              </p>

              <div class="mt-auto text-end">
                <span class="text-muted small">
                  {{ $t('paper.publishedAt') }}：{{ formatDate(item.published_at) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部狀態 -->
        <div v-if="isLoadingMore" class="text-center text-muted small mb-3">
          {{ $t('paper.loadingMore') }}
        </div>

        <div
          v-else-if="visibleOtherArticles.length < otherArticles.length"
          class="text-center text-muted small mb-3"
        >
          {{ $t('paper.scrollMore') }}
        </div>

        <div v-else class="text-center text-muted small mb-3">
          {{ $t('paper.allShown') }}
        </div>
      </section>

      <!-- 無資料 -->
      <div
        v-if="!featuredArticles.length && !otherArticles.length"
        class="text-center text-muted py-5"
      >
        {{ $t('paper.empty') }}
      </div>
    </template>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch
} from 'vue'
import { supabase } from '@/lib/supabase'
import { useRouter } from 'vue-router'

export default {
  name: 'Papers',
  setup () {
    
    const loading = ref(true)
    const errorMsg = ref('')
    const articles = ref([])
    const router = useRouter()

    // 一次顯示幾筆（下方列表用）
    const PAGE_SIZE = 10
    const itemsToShow = ref(PAGE_SIZE)
    const isLoadingMore = ref(false)

    // 🔍 搜尋相關
    const searchQuery = ref('')
    const quickKeywords = ref([
      '尖針',
      '鈍針',
      '玻尿酸',
      '唇部',
      '眼周',
      '面部美學分析',
      '皮膚困擾',
      '微表情'
    ])

    const handleSearchClick = () => {
      searchQuery.value = searchQuery.value.trim()
      // 每次重新搜尋，把顯示筆數重置
      itemsToShow.value = PAGE_SIZE
    }

    const applyQuickKeyword = (kw) => {
      searchQuery.value = kw
      itemsToShow.value = PAGE_SIZE
    }

    const clearSearch = () => {
      searchQuery.value = ''
      itemsToShow.value = PAGE_SIZE
    }

    const filteredArticles = computed(() => {
      const q = searchQuery.value.trim().toLowerCase()
      if (!q) return articles.value

      return articles.value.filter(a =>
        (a.title || '').toLowerCase().includes(q) ||
        (a.keyword || '').toLowerCase().includes(q)
      )
    })

    const featuredArticles = computed(() =>
      filteredArticles.value
        .filter(
          a =>
            a.sort !== null &&
            a.sort !== undefined &&
            a.sort >= 1 &&
            a.sort <= 9
        )
        .sort((a, b) => {
          const sa = a.sort == null ? 9999 : a.sort
          const sb = b.sort == null ? 9999 : b.sort
          return sa - sb
        })
    )

    const otherArticles = computed(() =>
      filteredArticles.value
        .filter(
          a =>
            a.sort === null ||
            a.sort === undefined ||
            a.sort < 1 ||
            a.sort > 9
        )
        .sort((a, b) => {
          const da = a.published_at || ''
          const db = b.published_at || ''
          return db.localeCompare(da)
        })
    )

    // ⭐ 這裡是「實際畫面上渲染的」那幾筆
    const visibleOtherArticles = computed(() =>
      otherArticles.value.slice(0, itemsToShow.value)
    )

    const getCoverUrl = (item) => {
      if (!item.cover_path) return null
      const { data } = supabase
        .storage
        .from('papers')
        .getPublicUrl(item.cover_path)
      return data.publicUrl
    }

    const displayTitle = (item) => item.title || ''

    const formatDate = (value) => {
      if (!value) return '-'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}/${m}/${day}`
    }

    const openArticle = (item) => {
      const p = String(item.content_path || '').trim()
      if (!p) return

      // ✅ 1) .html 當成 flip（強制走 R2）
      if (p.toLowerCase().endsWith('.html')) {
        const baseRaw = String(process.env.VUE_APP_ARTICLE_BASE_URL || '').trim()
        const base = baseRaw.replace(/\/$/, '') // 去掉尾巴 /

        if (!base) {
          console.error('[Papers] Missing VUE_APP_ARTICLE_BASE_URL in .env')
          // 這裡不 fallback，因為你說 public 會拿掉
          return
        }

        // 防呆：content_path 可能是
        // - article_36/TSK_CURVA.html
        // - /article_36/TSK_CURVA.html
        // - article/article_36/TSK_CURVA.html  (有人把 article/ 也存進去)
        const cleanP = p.replace(/^\/+/, '').replace(/^article\/+/i, '')

        const url = `${base}/article/${cleanP}`
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }

      // ✅ 2) 其他照舊（完全不動）
      router.push({
        name: 'AppPaperDetail',
        params: { id: item.id }
      })
    }


    // ⭐ 往下捲動時嘗試多顯示 10 筆
    const tryLoadMore = () => {
      if (isLoadingMore.value) return
      // 沒有更多了就不要再加
      if (itemsToShow.value >= otherArticles.value.length) return

      isLoadingMore.value = true
      // 這邊只是前端增加顯示數量，不需真的打 API，所以直接加就好
      itemsToShow.value += PAGE_SIZE
      isLoadingMore.value = false
    }

    const handleScroll = () => {
      if (loading.value) return
      // 粗暴版：判斷「捲到底部 80px 內」就觸發一次
      const scrollBottom = window.innerHeight + window.scrollY
      const docHeight =
        document.documentElement.scrollHeight ||
        document.documentElement.offsetHeight

      if (docHeight - scrollBottom < 80) {
        tryLoadMore()
      }
    }

    const loadArticles = async () => {
      loading.value = true
      errorMsg.value = ''

      try {
        const { data, error } = await supabase
          .from('papers')
          .select('id, title, sub_title, content_path, cover_path, sort, published_at, keyword')
          .order('sort', { ascending: true, nullsFirst: false })
          .order('published_at', { ascending: false })

        if (error) {
          console.error('[Papers] loadArticles error', error)
          throw error
        }

        articles.value = data || []
        // 每次重載資料也重置顯示筆數
        itemsToShow.value = PAGE_SIZE
      } catch (err) {
        console.error('[Papers] loadArticles exception', err)
        errorMsg.value = '文章載入失敗，稍後再試一次。'
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      loadArticles()
      window.addEventListener('scroll', handleScroll, { passive: true })
    })

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', handleScroll)
      if (io) {
        io.disconnect()
        io = null
      }
    })

    // 🔁 當搜尋字串變動時，自動把顯示筆數重置回 10
    watch(searchQuery, () => {
      itemsToShow.value = PAGE_SIZE
    })
    
        // 🔍 監控每一個 article-row 是否在 viewport 內
    const visibleMap = ref({}) // { [id]: boolean }
    let io = null

    const isVisible = (id) => !!visibleMap.value[id]

    const ensureObserver = () => {
      if (io) return
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const id = entry.target.dataset.articleId
            if (!id) return

            // ✅ 只要「第一次看到」就標記為可見，之後就不要再改回去了
            if (entry.isIntersecting) {
              visibleMap.value = {
                ...visibleMap.value,
                [id]: true
              }
              // 看過一次就不用再觀察，省一點效能
              io.unobserve(entry.target)
            }
          })
        },
        {
          root: null,
          threshold: 0.1
        }
      )
    }

    const registerArticleEl = (el, id) => {
      // Vue 在 unmount 時也會呼叫一次，這時 el 會是 null
      if (!el) return
      ensureObserver()
      el.dataset.articleId = id
      io.observe(el)
    }


    return {
      loading,
      errorMsg,
      searchQuery,
      quickKeywords,
      handleSearchClick,
      applyQuickKeyword,
      clearSearch,
      featuredArticles,
      otherArticles,
      visibleOtherArticles,
      isLoadingMore,
      getCoverUrl,
      displayTitle,
      formatDate,
      openArticle,
      registerArticleEl,
      isVisible
    }
  }
}
</script>

<style scoped>
.papers-page {
  max-width: 900px;
}

/* 搜尋列靠中 */
.search-bar-wrapper {
  max-width: 720px;
  margin: 0 auto;
}
.search-input-group input {
  border-radius: 999px 0 0 999px;
}
.search-input-group .btn {
  border-radius: 0 999px 999px 0;
}
.quick-keywords {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

/* 卡片 hover 效果 */
.clickable {
  cursor: pointer;
}
.clickable:hover {
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
  transition: all 0.15s ease-out;
}

/* 上方卡片封面 */
.card-img-top-wrapper {
  position: relative;
  padding-top: 66.66%; /* 3:2 比例 */
  overflow: hidden;
}
.card-img-top-wrapper .card-img-top,
.card-img-top-wrapper .placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.papers-page {
  padding-left: 20px;
  padding-right: 20px;
}
.article-card .card-body,
.article-card .card-title {
  text-align: left;
}

/* 下方列表縮圖 */
.thumb-wrapper {
  width: 120px;
  height: 80px;
  border-radius: 0.5rem;
  overflow: hidden;
}
.thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* placeholder 共用樣式 */
.placeholder {
  background: #f5f5f5;
}

/* 文字多行截斷 */
.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.text-truncate-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.article-row {
  background-color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}
.article-row:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}
.article-row h6 {
  text-align: left;
}
.article-row p {
  text-align: left;
}
.article-row {
  background-color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);

  /* 進出視窗動畫的基礎狀態：預設是「還沒看見」 */
  opacity: 0;
  transform: translateX(40px);
  transition:
    opacity 0.4s ease-out,
    transform 0.4s ease-out;
}

.article-row.is-visible {
  opacity: 1;
  transform: translateX(0);
}

.article-row:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}

</style>
